import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useParams, useHistory } from 'react-router-dom';
import MetaData from '../../layouts/MetaData';
import { DR_API, errMsg, fmtDate, fmtDateTime, patientLabel, typeLabel } from '../../components/DeviceRequests/deviceRequestApi';
import { useSortableTable, SortableTh, personName } from '../../components/SortableTable';
import { isObjectId } from '../../utils/apiError';

const BATCH_PRINT_SORT_COLUMNS = {
    patient: { type: 'name', value: (r) => personName(r?.patient) },
    dob: { type: 'date', value: (r) => r?.patient?.DOB },
    emrId: { type: 'string', value: (r) => r?.patient?.emrId },
    provider: { type: 'name', value: (r) => r?.providerName },
    prescribed: { type: 'date', value: (r) => r?.datePrescribed },
    status: { type: 'string', value: (r) => r?.status },
};

/** Printable batch sheet (no app chrome; window.print()). */
const BatchPrint = () => {
    const { id } = useParams();
    const history = useHistory();
    const [data, setData] = useState(null);
    const [error, setError] = useState('');
    useEffect(() => {
        if (!isObjectId(id)) { setError('This batch could not be found.'); return; }
        axios.get(`${DR_API}/batches/${id}`).then((r) => setData(r.data)).catch((err) => setError(errMsg(err)));
    }, [id]);
    const tableSort = useSortableTable(data?.requests, { id: 'deviceRequests.batchPrint', columns: BATCH_PRINT_SORT_COLUMNS, defaultSort: { key: 'patient', dir: 'asc' } });
    if (error) return <div className="alert alert-danger m-3">{error}</div>;
    if (!data) return <div className="m-3">Loading…</div>;
    const { batch, requests } = data;
    return (
        <div className="container-fluid p-4 bg-white" data-testid="dr-batch-print">
            <MetaData title={`Batch ${batch.batchNo}`} />
            <style>{'@media print { .no-print { display: none !important; } }'}</style>
            <div className="no-print mb-3">
                <button type="button" className="btn btn-primary btn-sm mr-2" onClick={() => window.print()}>Print</button>
                <button type="button" className="btn btn-link btn-sm" onClick={() => history.goBack()}>Back</button>
            </div>
            <h4>Device Request Batch {batch.batchNo}</h4>
            <div className="mb-3">{batch.organizationId?.name} · {batch.date} · sent by {batch.createdByName} {fmtDateTime(batch.createdAt)} · {batch.count} request(s)</div>
            <table className="table table-bordered table-sm">
                <thead><tr><th>#</th><SortableTh sortKey="patient" sort={tableSort}>Patient</SortableTh><SortableTh sortKey="dob" sort={tableSort}>DOB</SortableTh>
                    <SortableTh sortKey="emrId" sort={tableSort}>EHR-ID</SortableTh><th>Device(s)</th><SortableTh sortKey="provider" sort={tableSort}>Provider</SortableTh>
                    <SortableTh sortKey="prescribed" sort={tableSort}>Prescribed</SortableTh><th>Dx</th><th>Consent</th><th>Delivery</th><th>Phone</th>
                    <SortableTh sortKey="status" sort={tableSort}>Status / IMEI</SortableTh></tr></thead>
                <tbody>
                    {tableSort.rows.map((r, i) => (
                        <tr key={r._id}>
                            <td>{i + 1}</td><td>{patientLabel(r.patient)}</td><td>{fmtDate(r.patient?.DOB)}</td><td>{r.patient?.emrId}</td>
                            <td>{(r.deviceTypes || []).map(typeLabel).join(', ')}</td><td>{r.providerName}</td><td>{fmtDate(r.datePrescribed)}</td>
                            <td>{(r.diagnosisCodes || []).join(', ')}</td>
                            <td>{r.consent?.consentGiven ? `Yes, ${r.consent.consentMethod} ${fmtDate(r.consent.consentDate)}` : 'No'}</td>
                            <td>{r.delivery?.method === 'ship' ? `Ship: ${[r.delivery.address, r.delivery.line2, r.delivery.city, r.delivery.state, r.delivery.zipCode].filter(Boolean).join(', ')}` : 'In clinic'}</td>
                            <td>{r.phone}{r.phoneVerified ? ' ✓' : ''}</td>
                            <td>{r.status}{(r.items || []).filter((it) => it.imei).map((it) => <div key={it._id} className="small">{typeLabel(it.deviceType)} {it.imei}</div>)}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
};

export default BatchPrint;
